import { Location } from '@angular/common';
import { EventEmitter, inject, Injector, isDevMode, OnDestroy, Service } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { BaasI18n } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';

import { BarActionButtonOptions } from '../bar-action-button/bar-action-button-options.model';
import { BarDeviceType } from './bar-device-type.enum';
import { BarLayoutInternalGvkService } from './bar-layout-internal-gvk.service';
import { BarLayoutSection } from './bar-layout-section.enum';
import { BarLayoutSectionInjection } from './bar-layout-section-injection.model';
import { BarLayoutState } from './bar-layout-state.contract';
import { BarMenuState } from './bar-menu-state.type';
import { _BarBurgerprofielEngine } from './burgerprofiel/bar-burgerprofiel-engine-provider';
import { BAAS_LAYOUT_STATE } from './layout-tokens';

/** Internal BAR layout service NOT EXPOSED */
@Service()
export class _BarInternalLayoutService implements OnDestroy {
    readonly #injector = inject(Injector);
    readonly #location = inject(Location);
    readonly #translate = inject(BaasI18n);
    readonly #gvk = inject(BarLayoutInternalGvkService);
    readonly #burgerprofielEngine = inject(_BarBurgerprofielEngine);

    readonly #layoutState = inject<BarLayoutState>(BAAS_LAYOUT_STATE);
    /** @deprecated This indicates that the consumer is running in legacy modus. */
    legacyConsumer = false;
    injectSection = new EventEmitter<BarLayoutSectionInjection<any>>();
    clearSection = new EventEmitter<BarLayoutSection>();
    internalMainAction: BarActionButtonOptions;
    internalContentActions$: Observable<BarActionButtonOptions[]>;
    /** Internal content actions used for frameworking purposes. */
    #internalContentActions$$ = new BehaviorSubject<BarActionButtonOptions[]>([]);
    /** The last app loading requested value. */
    lastAppLoadingRequest: boolean;
    /** Whether the app loader processing is pending. */
    appLoadingPending$$ = new BehaviorSubject<boolean>(true);
    /** Whether the first app loader appearance is still on. */
    firstAppLoading$$ = new BehaviorSubject<boolean>(true);
    deviceType$$ = new BehaviorSubject<BarDeviceType | undefined>(undefined);

    readonly box = !this.#burgerprofielEngine.visible;

    get smallDevice(): boolean {
        return window.innerWidth < 1200;
    }

    get backAction(): BarActionButtonOptions {
        return {
            action: () => {
                this.back();
            },
            title: this.#translate.get('DOMAIN_BAR_LAYOUT.BACK'),
            icon: {
                class: 'fas fa-arrow-left',
                srText: this.#translate.get('DOMAIN_BAR_LAYOUT.BACK'),
                iconOnly: true
            }
        };
    }

    // avoid IOC cyclic dependency error
    get #core(): BarCoreService {
        return this.#injector.get(BarCoreService);
    }

    // actual delayed execution timeout
    #timeoutResult: number;
    // pipeline of actions to be executed after current run
    #actionPipeline: (() => void)[] = [];

    ngOnDestroy(): void {
        // complete all behavior subjects
        this.#internalContentActions$$.complete();
        this.appLoadingPending$$.complete();
        this.firstAppLoading$$.complete();
        this.deviceType$$.complete();
        this.injectSection.unsubscribe();
        this.clearSection.unsubscribe();
    }

    /** Queues an action to be executed in next ng iteration (in case dev mode). */
    queueAction(action: () => void): void {
        if (isDevMode()) {
            this.#actionPipeline.push(action);

            if (!this.#timeoutResult) {
                // NOTE avoid value has changed after it was checked issues
                // one timeout to cause one change detection (group all layout actions together)
                this.#timeoutResult = window.setTimeout(() => {
                    this.#actionPipeline.forEach((ac) => {
                        ac();
                    });
                    this.#timeoutResult = undefined;
                    this.#actionPipeline = [];
                });
            }
        } else {
            action();
        }
    }

    back(): void {
        this.#location.back();
    }

    setMenuState(state: BarMenuState): void {
        document.documentElement.setAttribute('data-bar-menu-state', state);
        // gvk will give us the menu state
        this.#gvk.persistMenuState(state);
    }

    /** Sets the internal content actions. */
    setInternalContentActions(internalContentActions: BarActionButtonOptions[]): void {
        this.#internalContentActions$$.next(internalContentActions);
    }

    init(): void {
        // this will define the icon button with the home icon
        this.internalMainAction = {
            title: this.#translate.get('DOMAIN_BAR_LAYOUT.MAINSCREEN'),
            icon: {
                class: 'fas fa-home',
                srText: this.#translate.get('DOMAIN_BAR_LAYOUT.MAINSCREEN'),
                iconOnly: true
            }
        };

        if (this.#layoutState.homeUrl != null) {
            // use provides an override for the layout module use this (which is href for now)
            this.internalMainAction.href = this.#layoutState.homeUrl;
        } else {
            this.internalMainAction.action = () => {
                // just use default core functionality if no override
                this.#core.navigateHome();
            };
        }

        this.internalContentActions$ = this.#internalContentActions$$.asObservable();
    }
}
